import React from 'react';
import { UpdateWorkspace } from '../../../shared/types/workspaceTypes';
import { FlexCol } from '../../../shared/FlexCol';
import { Body, Label } from '../../../shared/styled-components/styled_text';
import { FlexRow } from '../../../shared/FlexRow';
import dayjs from 'dayjs';
import { Clipboard } from '../../../common/components/Clipboard';
import { TextButton } from '../../../shared/styled-components/styled_buttons';
import { OnboardingStep1PreviewCompanyInfo } from './pc/OnboardingStep1PreviewCompanyInfo';
import styled, { css } from 'styled-components';
import { useKakaoMap } from '../../../common/components/KakaoMap/useKakaoMap';

interface Props {
  data: UpdateWorkspace;
}

export const GuidePreview: React.FC<Props> = ({ data }) => {
  const mapProps = data.location
    ? {
        icon: 'location_on',
        map: {
          x: data.location.x,
          y: data.location.y,
          zoom: data.location.zoom_lv,
          visible: true,
        },
      }
    : undefined;

  const { map } = useKakaoMap(
    { width: '29rem', height: '16rem', display: 'flex', justifyContent: 'center', alignItems: 'center' },
    data.location ? { x: data.location.x, y: data.location.y } : undefined,
    data.location?.zoom_lv,
  );

  const handleCopyAddress = () => {
    void navigator.clipboard.writeText(data.location.address);
  };

  return (
    <Container>
      <FlexCol width={'100%'} align={'flex-start'} gap={'0.4rem'} style={{ boxSizing: 'border-box' }}>
        <Body size={'l'}>
          <span style={{ fontWeight: '600', lineHeight: '150%' }}>지원자</span>님, 안녕하세요.
        </Body>
        <FlexRow width={'100%'} justify={'flex-start'} align={'center'}>
          <CompanyName $type={'title'} $filled={!!data.name}>
            {!data.name ? '회사 이름' : data.name}
          </CompanyName>
          <Body size={'l'}>입니다.</Body>
        </FlexRow>
        <Body size={'m'}>확정된 일정과 정보를 안내해드려요.</Body>
      </FlexCol>
      <ScheduleContainer>
        <ScheduleContentItem>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            일정
          </Label>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            {dayjs().format('YYYY.MM.DD(ddd)')}
          </Label>
        </ScheduleContentItem>
        <ScheduleContentItem>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            단계
          </Label>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            1차 인터뷰
          </Label>
        </ScheduleContentItem>
        <ScheduleContentItem>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            팀
          </Label>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            기획팀
          </Label>
        </ScheduleContentItem>
        <ScheduleContentItem>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            직무
          </Label>
          <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
            기획 PM
          </Label>
        </ScheduleContentItem>
        <ScheduleCharacter>
          <img src={'/images/workspace-wizard/onboarding_step1_preview_character.png'} alt={'schedule character'} />
        </ScheduleCharacter>
      </ScheduleContainer>
      <CompanyInfoContainer>
        <FlexCol width={'100%'} align={'flex-start'} gap={'0.8rem'}>
          <FlexRow width={'100%'} justify={'space-between'} align={'center'}>
            <FlexRow width={'fit-content'} justify={'flex-start'} align={'center'} gap={'0.4rem'}>
              <PlaceIcon />
              <Label size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
                인터뷰 장소
              </Label>
            </FlexRow>
            <Clipboard data={`${data.location.address} ${data.location.address_detail}`} tooltip={'복사 했어요'} id={'map'}>
              <TextButton
                color={'mono'}
                size={'xs'}
                icon={{ src: '/images/common/content_copy.svg', slot: 'end' }}
                onClick={handleCopyAddress}
              >
                주소 복사
              </TextButton>
            </Clipboard>
          </FlexRow>
          <FlexCol width={'100%'} align={'flex-start'} gap={'0.2rem'} style={{ paddingLeft: '2.2rem', boxSizing: 'border-box' }}>
            <Body
              size={'s'}
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box',
                color: `var(--content-color-${!data.location.address && !data.location.address_detail ? 'tertiary' : ''})`,
                backgroundColor: 'var(--mono-op-12)',
                paddingInline: '0.6rem',
                borderRadius: '4px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
              }}
            >
              {!data.location.address && !data.location.address_detail
                ? '위치정보를 작성하면 자동으로 입력됩니다.'
                : `${data.location.address} ${data.location.address_detail ?? ''}`}
            </Body>
            <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.4rem'}>
              <img src={'/images/common/error_fill.svg'} alt={'error icon'} style={{ width: '1.6rem', height: '1.6rem' }} />
              <Body
                size={'s'}
                style={{
                  color: ' var(--content-color-tertiary)',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                }}
              >
                지도를 클릭하면 카카오맵에서 볼 수 있어요.
              </Body>
            </FlexRow>
          </FlexCol>
          {mapProps && <MapWrap $visible={!!mapProps?.map?.visible}>{map}</MapWrap>}
        </FlexCol>
        <OnboardingStep1PreviewCompanyInfo
          style={{ boxSizing: 'border-box' }}
          orgName={data.name}
          companyAddress={`${data.location.address} ${data.location.address_detail ?? ''}`}
          orgContact={data.contact.officer_contact}
        />
      </CompanyInfoContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100% - 5.6rem);
  padding: 2.4rem 1.6rem;
  overflow-y: scroll;

  display: flex;
  flex-direction: column;
`;

const CompanyName = styled.div<{ $type: 'title' | 'content' | 'subtitle'; $filled?: boolean }>`
  width: fit-content;
  height: fit-content;
  background: var(--mono-op-12);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: var(--content-color-${props => (props.$filled ? 'primary' : 'tertiary')});

  ${props =>
    props.$type !== 'subtitle' &&
    css`
      padding-inline: 0.4rem;
      border-radius: 4px;
    `}

  ${props =>
    props.$type === 'title'
      ? css`
          max-width: 16rem;
          font-size: 1.6rem;
          font-weight: 600;
          line-height: 150%;
        `
      : css`
          max-width: 12rem;
          font-size: 1.4rem;
          font-weight: 500;
          line-height: 160%;
        `}
`;

const ScheduleContainer = styled.div`
  width: 100%;
  margin-top: 1.6rem;
  margin-bottom: 1.2rem;
  padding: 1.2rem;
  background: var(--primary-op-08);
  border-radius: 8px;
  box-sizing: border-box;
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.4rem;
`;

const ScheduleContentItem = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 0.4rem;

  > div:first-child {
    min-width: 4rem;
  }

  > div:last-child {
    display: flex;
    justify-content: flex-start;
    padding-inline: 0.4rem;
    background: var(--mono-op-12);
    border-radius: 4px;
  }
`;

const ScheduleCharacter = styled.div`
  width: 5.4rem;
  height: 4.6rem;
  position: absolute;
  right: 1.6rem;
  bottom: -1.2rem;
`;

const CompanyInfoContainer = styled.div`
  width: 100%;
  height: fit-content;
  padding: 1.6rem 1.2rem;
  background: var(--background-1);
  border-radius: 16px;
  box-sizing: border-box;
`;

const PlaceIcon = styled.div`
  width: 1.8rem;
  height: 1.8rem;
  mask-image: url('/images/common/location_on.svg');
  background-color: var(--content-color-tertiary);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const MapWrap = styled.div<{ $visible: boolean }>`
  width: 100%;
  display: ${props => (props.$visible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
`;
